import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Avatar, Button, Spin, Input } from 'antd';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import {
  ProfileLayout,
  UpcomingShowsSection,
  ShowPhotosSection,
  ShowCommentsSection,
  PeopleListSection,
  GenreTagsSection,
  AboutSection,
} from '../components/Profile';
import ProfileSection from '../components/Profile/sections/ProfileSection';
import type { ActionSheetItem } from '../components/SpecActionSheet/SpecActionSheet';
import type { CommentProps } from '../components/Comment/Comment';
import type { PhotoMetadata } from '../components/PhotoGallery/PhotoOverlayInfo';
import type { PersonItem } from '../components/Profile';
import { UserTag } from '../components/UserTag/UserTag';
import type { UserType } from '../components/UserTag/UserTag';
import UserTypeInfo from '../components/UserTypeInfo/UserTypeInfo';
import { DefaultAvatar, ChatIcon } from '../components/icons';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import SpecModal from '../components/SpecModal/SpecModal';
import { useShare } from '../hooks/useShare';
import { useAuth } from '../contexts/AuthContext';
import { useMyMusician, useContributorBands, useMusicianBands, useMusicianShows, useMusicianShowComments, useMusicianShowMedia } from '../api/queries';
import { delete_musician } from '../api';
import { getShowDateTime } from '../utils/showDateTime';
import './UserProfile.css';
import './EditProfile.css';

dayjs.extend(isSameOrAfter);

const DELETE_PASSCODE = 'deletemyprofile';

const MusicianProfile: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isProfileSaved = (location.state as any)?.profileSaved === true;

  const [toastMessage, setToastMessage] = useState<string | null>(isProfileSaved ? 'Profile Saved' : null);
  const [openInfoType, setOpenInfoType] = useState<UserType | null>(null);
  const share = useShare(setToastMessage);

  const { user } = useAuth();
  const { data: musician, isLoading: musicianLoading } = useMyMusician(!!user);

  // Real check, not a static badge: only show the Show Contributor tag if
  // this musician actually contributes to at least one band. Called before
  // the guard returns below (Rules of Hooks) - enabled handles musician
  // not being loaded yet.
  const { data: contributorBands } = useContributorBands(musician?.user_id, !!user);
  const isShowContributor = (contributorBands?.length ?? 0) > 0;

  const { data: musicianBands } = useMusicianBands(musician?.id);
  const { data: musicianShows } = useMusicianShows(musician?.id);
  const { data: musicianShowComments } = useMusicianShowComments(musician?.id);
  const { data: musicianShowMedia } = useMusicianShowMedia(musician?.id);

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deletePasscode, setDeletePasscode] = useState('');
  const [deleting, setDeleting] = useState(false);

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setDeletePasscode('');
  };

  const handleDeleteProfile = async () => {
    if (!musician) return;
    setDeleting(true);
    try {
      await delete_musician(musician.id);
      navigate('/home');
    } catch (error: any) {
      setToastMessage(error.message || 'Failed to delete musician profile');
      setDeleting(false);
    }
  };

  useEffect(() => {
    if (isProfileSaved) {
      window.history.replaceState({}, '');
    }
  }, [isProfileSaved]);

  // Format date to "Day Month Date" format (e.g., "Wed Sep 17")
  const formatDate = (dateString: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateString.split('T')[0].split('-').map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  // Format timestamp to "Mon Day Time" format (e.g., "Sep 16 3:29 PM")
  const formatTimestamp = (timestamp: string | { seconds: number } | { _seconds: number } | undefined) => {
    if (!timestamp) return '';

    // Handle Firestore Timestamp object (with seconds or _seconds)
    if (typeof timestamp === 'object') {
      const seconds = 'seconds' in timestamp ? timestamp.seconds : '_seconds' in timestamp ? (timestamp as { _seconds: number })._seconds : null;
      if (seconds) {
        return dayjs.unix(seconds).format('MMM D h:mm A');
      }
      return '';
    }

    // Handle ISO string or other date string
    const parsed = dayjs(timestamp);
    if (!parsed.isValid()) return '';
    return parsed.format('MMM D h:mm A');
  };

  // Extract display name from what might be an email (use part before @)
  const formatUserName = (name: string | undefined) => {
    if (!name) return 'Anonymous';
    if (name.includes('@')) return name.split('@')[0];
    return name;
  };

  // Filter musician shows into upcoming (today and future) and sort by date
  const today = dayjs().startOf('day');

  const allShowsTransformed = (musicianShows || [])
    .map(show => ({
      id: show.id,
      bandName: show.band_name,
      date: formatDate(show.date),
      rawDate: show.date,
      venue: show.venue_name || show.address || 'TBA',
      time: show.time,
      description: show.description,
      imageUrl: show.band_photo_url,
    }))
    .sort((a, b) => getShowDateTime(a.rawDate, a.time) - getShowDateTime(b.rawDate, b.time));

  const upcomingShows = allShowsTransformed
    .filter(show => dayjs(show.rawDate).startOf('day').isSameOrAfter(today))
    .map(({ rawDate, ...show }) => show);

  const showsToDisplay = upcomingShows;

  // Transform musician bands to PersonItem format
  const bands: PersonItem[] = (musicianBands || []).map(band => ({
    name: band.name,
    avatarUrl: band.photo_url,
    avatarShape: 'rounded' as const,
    route: `/bands/${band.id}`,
  }));

  // Transform show comments to CommentProps format
  const comments: Omit<CommentProps, 'className'>[] = (musicianShowComments || []).map(comment => {
    const rawUserId = comment.user_id || comment.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(comment.user_name),
      avatarUrl: comment.user_avatar_url,
      timestamp: formatTimestamp(comment.timestamp || comment.created_at || ''),
      userTags: (comment.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      text: comment.comment,
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      onUserClick: userId ? () => navigate(`/fans/${userId}`) : undefined,
      showEventInfo: !!(comment.band_name && comment.show_date && comment.venue_name),
      eventInfo: (comment.band_name && comment.show_date && comment.venue_name) ? {
        artistName: comment.band_name,
        artistAvatarUrl: comment.band_photo_url,
        date: formatDate(comment.show_date),
        venue: comment.venue_name,
      } : undefined,
    };
  });

  // Transform show media to photos array and metadata
  const photos: string[] = (musicianShowMedia || []).map(media => media.url);
  const photoMetadata: PhotoMetadata[] = (musicianShowMedia || []).map(media => {
    const rawUserId = media.user_id || media.created_by_id;
    const userId = rawUserId && rawUserId !== 'system' ? rawUserId : undefined;
    return {
      userName: formatUserName(media.user_name),
      userAvatarUrl: media.user_avatar_url,
      timestamp: formatTimestamp(media.timestamp || media.created_at || ''),
      userTags: (media.user_tags || []) as ('fan' | 'musician' | 'showContributor')[],
      userProfileUrl: userId ? `/fans/${userId}` : undefined,
      eventInfo: (media.band_name && media.show_date && media.venue_name) ? {
        artistName: media.band_name,
        artistAvatarUrl: media.band_photo_url,
        date: formatDate(media.show_date),
        venue: media.venue_name,
      } : undefined,
    };
  });

  // Not logged in - mirrors UserProfile.tsx's logged-out state
  if (!user) {
    return (
      <ProfileLayout profileType="musician" heroVariant="none" actionSheetItems={[]}>
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">Welcome to Spec</h2>
          <p className="user-profile__empty-subtitle">Sign in to view your musician profile.</p>
          <div className="user-profile__auth-buttons">
            <Button type="primary" size="large" shape="round" block onClick={() => navigate('/signin')}>
              Sign In
            </Button>
            <Button size="large" shape="round" block onClick={() => navigate('/signup')} className="user-profile__signup-btn">
              Create Account
            </Button>
          </div>
        </div>
      </ProfileLayout>
    );
  }

  if (musicianLoading) {
    return (
      <ProfileLayout profileType="musician" heroVariant="none" actionSheetItems={[]}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '64px 0' }}>
          <Spin size="large" />
        </div>
      </ProfileLayout>
    );
  }

  // Logged in, but no musician profile linked to this account
  if (!musician) {
    return (
      <ProfileLayout profileType="musician" heroVariant="none" actionSheetItems={[]}>
        <div className="user-profile__not-logged-in">
          <div className="user-profile__empty-avatar">
            <DefaultAvatar size={64} />
          </div>
          <h2 className="user-profile__empty-title">No Musician Profile</h2>
          <p className="user-profile__empty-subtitle">This account doesn't have a linked musician profile yet.</p>
        </div>
      </ProfileLayout>
    );
  }

  const fullName = `${musician.first_name} ${musician.last_name}`.trim();

  const actionItems: ActionSheetItem[] = [
    { key: 'share', label: 'Share', onClick: () => share({ title: fullName }) },
    { key: 'copy-link', label: 'Copy Link', onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); setToastMessage('Link Copied'); } },
    { key: 'edit-profile', label: 'Edit Profile', onClick: () => navigate('/edit-musician-profile') },
    { key: 'delete', label: 'Delete Profile', danger: true, onClick: () => setDeleteModalOpen(true) },
  ];

  return (
    <ProfileLayout
      profileType="musician"
      heroVariant="blurred"
      heroImageUrl={musician.photo_url}
      showPlayButton={!!musician.soundcloud_url}
      soundcloudUrl={musician.soundcloud_url}
      actionSheetItems={actionItems}
    >
      <div className="band-identity">
        <div className="band-identity__avatar">
          {musician.photo_url ? (
            <img className="band-identity__avatar-img band-identity__avatar-img--circle" src={musician.photo_url} alt={fullName} />
          ) : (
            <div className="band-identity__avatar-placeholder band-identity__avatar-placeholder--circle">
              <DefaultAvatar size={48} />
            </div>
          )}
        </div>
        <div className="band-identity__info">
          <h1 className="band-identity__name">{fullName}</h1>
          <div className="band-identity__badges">
            <UserTag type="musician" onClick={() => setOpenInfoType('musician')} />
            {isShowContributor && (
              <UserTag type="showContributor" onClick={() => setOpenInfoType('showContributor')} />
            )}
          </div>
        </div>
        <UserTypeInfo type="musician" open={openInfoType === 'musician'} onClose={() => setOpenInfoType(null)} />
        <UserTypeInfo type="showContributor" open={openInfoType === 'showContributor'} onClose={() => setOpenInfoType(null)} />
        {bands.length > 0 && (
          <div
            className="band-identity__members band-identity__members--rounded"
            onClick={() => document.getElementById('bands-section')?.scrollIntoView({ behavior: 'smooth' })}
          >
            <Avatar.Group size="default" max={{ count: 3, style: { backgroundColor: '#252B2D', color: '#fff', fontSize: 12, borderRadius: 4 } }}>
              {bands.map((band, index) => (
                <Avatar
                  key={index}
                  src={band.avatarUrl}
                  shape="square"
                  style={!band.avatarUrl ? { backgroundColor: '#252B2D' } : undefined}
                />
              ))}
            </Avatar.Group>
            <span className="band-identity__members-text">
              With {bands[0]?.name}{bands.length > 1 ? ' and more...' : ''}
            </span>
          </div>
        )}
      </div>
      <AboutSection text={musician.bio || 'No bio yet.'} />

      <UpcomingShowsSection
        shows={showsToDisplay}
        allShows={allShowsTransformed}
        showAddButton
        addShowIcon={<ChatIcon size="s" tone="black" />}
        onAddShow={() => navigate('/add-show')}
      />

      {photos.length > 0 ? (
        <ShowPhotosSection photos={photos} photoMetadata={photoMetadata} subtitle="Photos from shows" />
      ) : (
        <ProfileSection title="Show Photos" subtitle="Photos from shows">
          <div className="view-all-drawer__empty">No photos yet</div>
        </ProfileSection>
      )}

      {comments.length > 0 ? (
        <ShowCommentsSection comments={comments} showEventInfo />
      ) : (
        <ProfileSection title="Show Comments">
          <div className="view-all-drawer__empty">No comments yet</div>
        </ProfileSection>
      )}

      {bands.length > 0 ? (
        <PeopleListSection
          id="bands-section"
          title="Bands"
          people={bands}
          avatarShape="rounded"
          actionLabel="Add Band"
          actionIcon={<ChatIcon size="s" tone="black" />}
          onAction={() => navigate('/add-band')}
        />
      ) : (
        <ProfileSection title="Bands">
          <div className="view-all-drawer__empty">No bands listed</div>
        </ProfileSection>
      )}

      {musician.genres && musician.genres.length > 0 && (
        <GenreTagsSection genres={musician.genres} />
      )}
      {musician.instruments && musician.instruments.length > 0 ? (
        <GenreTagsSection title="Instruments" genres={musician.instruments} />
      ) : (
        <ProfileSection title="Instruments">
          <div className="view-all-drawer__empty">No instruments listed</div>
        </ProfileSection>
      )}

      <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />

      {/* Delete Profile Modal */}
      <SpecModal
        open={deleteModalOpen}
        onCancel={closeDeleteModal}
        title="Delete Profile"
        footer={null}
        closable={false}
        centered
        width={340}
      >
        <p className="edit-profile-modal-body">
          Type {DELETE_PASSCODE} to permanently delete your musician profile.
          This deletes your musician profile only - your Spec account stays intact.
        </p>
        <div className="edit-profile-delete-form">
          <span className="edit-profile-delete-label">Delete Passcode:</span>
          <Input
            placeholder={`Enter ${DELETE_PASSCODE} to delete`}
            value={deletePasscode}
            onChange={(e) => setDeletePasscode(e.target.value)}
          />
          <Button
            block
            size="large"
            shape="round"
            className="edit-profile-delete-btn"
            disabled={deletePasscode !== DELETE_PASSCODE || deleting}
            loading={deleting}
            onClick={handleDeleteProfile}
          >
            DELETE PROFILE
          </Button>
        </div>
      </SpecModal>
    </ProfileLayout>
  );
};

export default MusicianProfile;
